import { Check, X, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { FareOption } from "@/types/travel";
import { formatCurrency, cn } from "@/lib/utils";

interface FareSelectorProps {
  fareOptions: FareOption[];
  onSelect: (fare: FareOption) => void;
  selectedFare?: FareOption | null;
}

// 3-state policy icon: green check for unambiguously positive (free / included /
// positive bag count), red X for unambiguously negative (not allowed / not
// included), neutral dash for fee-based or unreported policies. Prevents the
// previous false-positive where "Changes for USD 70" showed a green check.
type PolicyState = 'positive' | 'negative' | 'neutral';

function policyState(value: string | number | boolean | undefined): PolicyState {
  if (value === false) return 'negative';
  if (value === true) return 'positive';
  const s = String(value ?? '').toLowerCase();
  if (!s) return 'neutral';
  if (s.includes('not allowed') || s.includes('not included') || s.includes('non-refundable')) {
    return 'negative';
  }
  if (s.startsWith('free ') || s.includes('included') || /^\d/.test(s)) {
    return 'positive';
  }
  // "Changes for USD 70", "Changes not reported", "Contact airline", etc.
  return 'neutral';
}

function PolicyIcon({ state }: { state: PolicyState }) {
  if (state === 'positive') return <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />;
  if (state === 'negative') return <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />;
  return <Minus className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />;
}

export function FareSelector({ fareOptions, onSelect, selectedFare }: FareSelectorProps) {
  // VOY-395: fare `type` is now a slug derived from the backend (typically
  // 'basic' | 'standard' | 'flex' from the heuristic naming, or a slugified
  // Duffel fare_brand_name like 'economy-flex-plus'). We match prefixes to
  // assign colors so branded names like "Economy Flex" still hit the purple
  // bucket, and any unrecognized brand falls through to neutral gray.
  const getFareColorClass = (fareType: string) => {
    if (fareType.includes('flex')) return 'border-purple-300 dark:border-purple-700';
    if (fareType.includes('standard') || fareType.includes('classic')) return 'border-blue-300 dark:border-blue-700';
    return 'border-gray-300 dark:border-gray-700';
  };

  const getFareHeaderClass = (fareType: string) => {
    if (fareType.includes('flex')) return 'bg-purple-50 dark:bg-purple-900/20';
    if (fareType.includes('standard') || fareType.includes('classic')) return 'bg-blue-50 dark:bg-blue-900/20';
    return 'bg-gray-50 dark:bg-gray-900/50';
  };

  // Grid columns follow the number of fares so 1/2/3/4/5 options render
  // symmetrically. Capped at 3 on md so a 5-fare row wraps to 3+2 instead of
  // cramming onto a single line.
  const gridCols = Math.min(fareOptions.length, 3);
  const gridColsClass = gridCols === 1 ? 'md:grid-cols-1'
    : gridCols === 2 ? 'md:grid-cols-2'
    : 'md:grid-cols-3';

  return (
    <div className={cn('grid gap-4', gridColsClass)}>
      {fareOptions.map((fare) => (
        <Card
          // Use offerId as the key — two fares can share the same synthetic
          // type slug (e.g. two "standard" fares for the same itinerary), but
          // offerId is always unique per Duffel offer.
          key={fare.offerId}
          className={cn(
            "relative overflow-hidden transition-all duration-200",
            getFareColorClass(fare.type),
            selectedFare?.offerId === fare.offerId && "ring-2 ring-primary"
          )}
        >
          <div className={cn("p-4", getFareHeaderClass(fare.type))}>
            <h3 className="font-semibold text-lg">{fare.name}</h3>
            {fare.description && (
              <p className="text-sm text-muted-foreground mt-1">{fare.description}</p>
            )}
            <div className="mt-3">
              <span className="text-2xl font-bold">{formatCurrency(fare.price)}</span>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {/* Change Policy */}
            <div className="flex items-start gap-2">
              <PolicyIcon state={policyState(fare.included.changePolicy)} />
              <div>
                <p className="text-sm font-medium">Changes</p>
                <p className="text-xs text-muted-foreground">{fare.included.changePolicy}</p>
              </div>
            </div>

            {/* Refund Policy */}
            <div className="flex items-start gap-2">
              <PolicyIcon state={policyState(fare.included.refundPolicy)} />
              <div>
                <p className="text-sm font-medium">Refunds</p>
                <p className="text-xs text-muted-foreground">{fare.included.refundPolicy}</p>
              </div>
            </div>

            {/* Carry-on Bags */}
            <div className="flex items-start gap-2">
              <PolicyIcon state={policyState(fare.included.carryOnBags)} />
              <div>
                <p className="text-sm font-medium">Carry-on</p>
                <p className="text-xs text-muted-foreground">{fare.included.carryOnBags}</p>
              </div>
            </div>

            {/* Checked Bags */}
            <div className="flex items-start gap-2">
              <PolicyIcon state={policyState(fare.included.checkedBags)} />
              <div>
                <p className="text-sm font-medium">Checked bags</p>
                <p className="text-xs text-muted-foreground">
                  {fare.included.checkedBags || 'Not included'}
                </p>
              </div>
            </div>

            {/* Seat Selection */}
            <div className="flex items-start gap-2">
              <PolicyIcon state={policyState(fare.included.seatSelection)} />
              <div>
                <p className="text-sm font-medium">Seat selection</p>
                <p className="text-xs text-muted-foreground">
                  {fare.included.seatSelection === false
                    ? 'Not included'
                    : fare.included.seatSelection || 'Included'}
                </p>
              </div>
            </div>

            {/* Priority Boarding */}
            {fare.included.priority !== undefined && (
              <div className="flex items-start gap-2">
                {fare.included.priority ? (
                  <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className="text-sm font-medium">Priority boarding</p>
                  <p className="text-xs text-muted-foreground">
                    {fare.included.priority ? 'Included' : 'Not included'}
                  </p>
                </div>
              </div>
            )}

            {/* Lounge Access */}
            {fare.included.lounge !== undefined && (
              <div className="flex items-start gap-2">
                {fare.included.lounge ? (
                  <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className="text-sm font-medium">Lounge access</p>
                  <p className="text-xs text-muted-foreground">
                    {fare.included.lounge ? 'Included' : 'Not included'}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 pt-0">
            <Button
              className="w-full"
              variant={selectedFare?.offerId === fare.offerId ? "default" : "outline"}
              onClick={() => onSelect(fare)}
            >
              {selectedFare?.offerId === fare.offerId ? 'Selected' : 'Select'}
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}